import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

// ─── Types ───────────────────────────────────────────────────────────────────

interface DropdownProps {
    /** The trigger element (any React node). The component injects `ref`, `onClick`, and `isOpen` as props. */
    trigger: React.ReactNode;
    children: React.ReactNode;
    /** Alignment of the menu relative to the trigger. Default: 'end' */
    align?: 'start' | 'end';
    className?: string;
}

interface MenuItemProps {
    children: React.ReactNode;
    onClick?: () => void;
    /** Renders the item in danger (red) colour */
    danger?: boolean;
    disabled?: boolean;
    className?: string;
}

interface MenuSectionProps {
    label: string;
    children?: React.ReactNode;
}

interface MenuDividerProps {}

interface MenuTextProps {
    children: React.ReactNode;
}

// ─── Menu item ────────────────────────────────────────────────────────────────

const menuItemBase: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    borderRadius: 7,
    margin: '1px 4px',
    transition: 'background 0.12s',
    border: 'none',
    background: 'none',
    width: 'calc(100% - 8px)',
    textAlign: 'left' as const,
    textDecoration: 'none',
};

export const DropdownItem: React.FC<MenuItemProps> = ({
    children,
    onClick,
    danger = false,
    disabled = false,
    className = '',
}) => {
    const [hovered, setHovered] = useState(false);

    const color = disabled
        ? 'var(--bs-secondary-color)'
        : danger
        ? 'var(--bs-danger)'
        : 'var(--bs-body-color)';

    const bg = hovered && !disabled
        ? danger
            ? 'rgba(var(--bs-danger-rgb), 0.08)'
            : 'rgba(var(--bs-primary-rgb), 0.06)'
        : 'transparent';

    return (
        <button
            type="button"
            className={className}
            style={{ ...menuItemBase, color, background: bg, opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
            onClick={disabled ? undefined : onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            {children}
        </button>
    );
};

export const DropdownDivider: React.FC<MenuDividerProps> = () => (
    <div style={{ height: 1, background: 'var(--bs-border-color)', margin: '4px 0' }} />
);

export const DropdownSection: React.FC<MenuSectionProps> = ({ label }) => (
    <p
        style={{
            fontSize: 10,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: 'var(--bs-secondary-color)',
            padding: '6px 14px 2px',
            margin: 0,
        }}
    >
        {label}
    </p>
);

export const DropdownText: React.FC<MenuTextProps> = ({ children }) => (
    <p
        style={{
            fontSize: 12,
            color: 'var(--bs-secondary-color)',
            padding: '4px 14px',
            margin: 0,
        }}
    >
        {children}
    </p>
);

// ─── Menu shell ───────────────────────────────────────────────────────────────

const menuStyle: React.CSSProperties = {
    background: 'var(--bs-body-bg)',
    border: '1px solid var(--bs-border-color)',
    borderRadius: 12,
    boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
    padding: '4px 0',
    minWidth: 180,
    zIndex: 9999,
    position: 'fixed' as const,
    overflow: 'hidden',
};

// ─── Root Dropdown ────────────────────────────────────────────────────────────

export const Dropdown: React.FC<DropdownProps> = ({
    trigger,
    children,
    align = 'end',
    className = '',
}) => {
    const [open, setOpen] = useState(false);
    const [menuStyle2, setMenuStyle2] = useState<React.CSSProperties>({});
    const triggerRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const close = useCallback(() => setOpen(false), []);

    // Position the menu relative to the trigger via portal
    const positionMenu = useCallback(() => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        const menuWidth = menuRef.current?.offsetWidth ?? 200;
        const menuHeight = menuRef.current?.offsetHeight ?? 200;
        const windowW = window.innerWidth;
        const windowH = window.innerHeight;
        const spaceBelow = windowH - rect.bottom;
        const spaceAbove = rect.top;
        const openUp = spaceBelow < menuHeight + 8 && spaceAbove > spaceBelow;
        let top = openUp ? rect.top - menuHeight - 4 : rect.bottom + 4;
        top = Math.max(8, Math.min(top, windowH - menuHeight - 8));
        let left = align === 'end' ? rect.right - menuWidth : rect.left;
        left = Math.max(8, Math.min(left, windowW - menuWidth - 8));
        setMenuStyle2({ top, left });
    }, [align]);

    const toggle = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setOpen((prev) => {
            if (!prev) setTimeout(positionMenu, 0);
            return !prev;
        });
    }, [positionMenu]);

    // Close on outside click or scroll
    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (
                menuRef.current && !menuRef.current.contains(e.target as Node) &&
                triggerRef.current && !triggerRef.current.contains(e.target as Node)
            ) {
                close();
            }
        };
        document.addEventListener('mousedown', handler);
        document.addEventListener('scroll', close, true);
        return () => {
            document.removeEventListener('mousedown', handler);
            document.removeEventListener('scroll', close, true);
        };
    }, [open, close]);

    // Wrap each DropdownItem's onClick to also close the menu
    const wrappedChildren = React.Children.map(children, (child) => {
        if (React.isValidElement(child) && child.type === DropdownItem) {
            const el = child as React.ReactElement<MenuItemProps>;
            return React.cloneElement(el, {
                onClick: el.props.onClick
                    ? () => { el.props.onClick!(); close(); }
                    : close,
            });
        }
        return child;
    });

    return (
        <div ref={triggerRef} className={`d-inline-flex ${className}`} style={{ position: 'relative' }}>
            {/* Clone trigger to inject onClick */}
            {React.isValidElement(trigger)
                ? React.cloneElement(trigger as React.ReactElement<any>, { onClick: toggle })
                : <div onClick={toggle}>{trigger}</div>
            }

            {open && createPortal(
                <div
                    ref={menuRef}
                    style={{ ...menuStyle, ...menuStyle2 }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {wrappedChildren}
                </div>,
                document.body
            )}
        </div>
    );
};

export default Dropdown;
